import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import OrderDetailsPage from "./OrderDetailsPage";
import { adminFetch } from "@/lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrderDetailsPreviewRoute() {
  const navigate = useNavigate();
  const { orderNumber } = useParams();
  const [reportId, setReportId] = useState(null);
  const [followUpEnabled, setFollowUpEnabled] = useState(null);
  const [followUpBusy, setFollowUpBusy] = useState(false);

  useEffect(() => {
    const handleReviewReport = (event) => {
      const link = event.target.closest('a[data-testid="action-review-report"]');
      if (!link) return;
      const match = link.getAttribute("href")?.match(/\/api\/reports\/(\d+)\/render$/);
      if (!match) return;
      event.preventDefault();
      navigate(`/admin/report-preview/${encodeURIComponent(orderNumber)}/${match[1]}`);
    };

    document.addEventListener("click", handleReviewReport);
    return () => document.removeEventListener("click", handleReviewReport);
  }, [navigate, orderNumber]);

  useEffect(() => {
    let cancelled = false;
    adminFetch(`${API}/api/orders/${encodeURIComponent(orderNumber)}`)
      .then((res) => res.ok ? res.json() : null)
      .then((order) => {
        if (cancelled || !order) return;
        if (order.reports?.length) setReportId(order.reports[0].id);
        setFollowUpEnabled(Boolean(order.follow_up_opt_in) && !order.marketing_unsubscribed_at);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [orderNumber]);

  const disableFollowUp = async () => {
    if (followUpBusy || followUpEnabled === false) return;
    const confirmed = window.confirm(`Turn OFF the automatic five-day follow-up for ${orderNumber}?`);
    if (!confirmed) return;
    setFollowUpBusy(true);
    try {
      const res = await adminFetch(`${API}/api/orders/${encodeURIComponent(orderNumber)}/unsubscribe`, { method: "POST" });
      if (!res.ok) throw new Error(await res.text());
      setFollowUpEnabled(false);
    } catch (err) {
      window.alert(`Could not turn off the five-day follow-up. ${err?.message || "Please try again."}`);
    } finally {
      setFollowUpBusy(false);
    }
  };

  return (
    <div>
      <div style={{ padding: "18px 32px 0", display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {followUpEnabled !== null ? (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              data-testid="five-day-follow-up-status"
              style={{
                padding: "9px 12px",
                borderRadius: 10,
                border: followUpEnabled ? "1px solid #A7D7A9" : "1px solid #D6D6D6",
                background: followUpEnabled ? "#EAF7EA" : "#F1F1F1",
                color: followUpEnabled ? "#236B2A" : "#666",
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              Five-Day Follow-Up: {followUpEnabled ? "ON" : "OFF"}
            </span>
            {followUpEnabled ? (
              <button
                type="button"
                onClick={disableFollowUp}
                disabled={followUpBusy}
                data-testid="disable-five-day-follow-up"
                style={{
                  padding: "9px 12px",
                  borderRadius: 10,
                  border: "1px solid #D7A4A4",
                  background: "#FFF4F4",
                  color: "#9B2C2C",
                  fontWeight: 800,
                  fontSize: 13,
                  cursor: followUpBusy ? "wait" : "pointer",
                }}
              >
                {followUpBusy ? "Turning Off..." : "Turn Off"}
              </button>
            ) : null}
          </div>
        ) : null}
        {reportId ? (
          <Link
            to={`/admin/follow-up-test/${encodeURIComponent(orderNumber)}/${reportId}`}
            data-testid="five-day-follow-up-control"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "10px 15px",
              borderRadius: 10,
              border: "1px solid #C9BFEE",
              background: "#F0ECFB",
              color: "#5B21B6",
              fontWeight: 800,
              textDecoration: "none",
              fontSize: 14,
            }}
          >
            Five-Day Follow-Up
          </Link>
        ) : null}
        <Link
          to={`/admin/orders/${encodeURIComponent(orderNumber)}/teacher-comments`}
          data-testid="edit-report-content"
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "10px 15px",
            borderRadius: 10,
            background: "#6A4FC0",
            color: "#fff",
            fontWeight: 800,
            textDecoration: "none",
            fontSize: 14,
          }}
        >
          Edit Report Content
        </Link>
      </div>
      <OrderDetailsPage />
    </div>
  );
}
